import { getReadChapters } from "../components/ChapterProgress/useChapterProgress";
import { getBookNameByChapterId, parseChapterId } from "./bookNames";
import {
  getReadingStorageKey,
  getReadingStorageUser,
  normalizeReadingUid,
} from "./readingStorageScope";

const STORAGE_LAST_READ = "bible_last_read";
const STORAGE_RECENT_CHAPTERS = "bible_recent_chapters";
const STORAGE_CURRENT_READER = "bible_current_reader_chapter";
export const EVENT_LAST_READ_CHANGED = "bible_last_read_changed";

export const readingTimestamp = (item) => {
  const raw = item?.timestamp;
  if (typeof raw === "number" && Number.isFinite(raw)) return raw;
  if (typeof raw === "string" && raw.trim()) {
    const asNumber = Number(raw);
    if (Number.isFinite(asNumber) && asNumber > 0) return asNumber;
    const parsed = Date.parse(raw);
    if (Number.isFinite(parsed)) return parsed;
  }
  return 0;
};

export const saveLastReadChapter = (
  {
    chapterId,
    bookName,
    chapterNumber,
    title,
    editionId = "zh",
  },
  uid
) => {
  if (!chapterId) return;

  const parsed = parseChapterId(chapterId);
  const resolvedChapterNumber = parsed.chapterNumber || chapterNumber || 1;
  const resolvedBookName =
    bookName || getBookNameByChapterId(chapterId, editionId === "en") || "";

  const item = {
    chapterId,
    bookName: resolvedBookName,
    chapterNumber: resolvedChapterNumber,
    title: (title || "").replace(/^#\s*/, ""),
    editionId,
    timestamp: Date.now(),
  };

  try {
    localStorage.setItem(
      getReadingStorageKey(STORAGE_LAST_READ, uid),
      JSON.stringify(item)
    );

    // Update recent chapters (up to 8 items, unique by chapterId)
    const rawRecent = localStorage.getItem(
      getReadingStorageKey(STORAGE_RECENT_CHAPTERS, uid)
    );
    let recent = rawRecent ? JSON.parse(rawRecent) : [];
    if (!Array.isArray(recent)) recent = [];

    // Filter out same chapterId
    recent = recent.filter((r) => r.chapterId !== chapterId);
    // Unshift newest
    recent.unshift(item);
    if (recent.length > 8) {
      recent = recent.slice(0, 8);
    }
    localStorage.setItem(
      getReadingStorageKey(STORAGE_RECENT_CHAPTERS, uid),
      JSON.stringify(recent)
    );
    setCurrentReaderChapter(item, uid);

    window.dispatchEvent(new Event(EVENT_LAST_READ_CHANGED));
  } catch (e) {
    // ignore localStorage errors
  }
};

const resolveReaderScope = (uid) =>
  uid !== undefined ? normalizeReadingUid(uid) : getReadingStorageUser();

export const setCurrentReaderChapter = (item, uid) => {
  if (typeof sessionStorage === "undefined" || !item?.chapterId) return;
  try {
    const scope = resolveReaderScope(uid);
    sessionStorage.setItem(
      getReadingStorageKey(STORAGE_CURRENT_READER, scope),
      JSON.stringify({
        chapterId: item.chapterId,
        bookName: item.bookName || "",
        chapterNumber: item.chapterNumber || null,
        title: item.title || "",
        editionId: item.editionId || "zh",
        timestamp: item.timestamp || Date.now(),
        uid: scope,
      })
    );
  } catch (e) {
    // ignore sessionStorage errors
  }
};

export const clearCurrentReaderChapter = (uid) => {
  if (typeof sessionStorage === "undefined") return;
  try {
    const scope = resolveReaderScope(uid);
    sessionStorage.removeItem(getReadingStorageKey(STORAGE_CURRENT_READER, scope));
    sessionStorage.removeItem(STORAGE_CURRENT_READER);
  } catch (e) {
    // ignore sessionStorage errors
  }
};

export const getCurrentReaderChapter = (uid) => {
  if (typeof sessionStorage === "undefined") return null;
  try {
    const scope = resolveReaderScope(uid);
    const key = getReadingStorageKey(STORAGE_CURRENT_READER, scope);
    let raw = sessionStorage.getItem(key);
    // Signed-in reads must not fall back to a leftover unscoped key from
    // another account. Guests may still read the historical unscoped slot.
    if (!raw && !scope) {
      raw = sessionStorage.getItem(STORAGE_CURRENT_READER);
    }
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.chapterId) return null;
    const storedScope = normalizeReadingUid(parsed.uid);
    if (storedScope !== scope) return null;
    return parsed;
  } catch (e) {
    return null;
  }
};

export const getLastReadChapter = (uid) => {
  try {
    const raw = localStorage.getItem(getReadingStorageKey(STORAGE_LAST_READ, uid));
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

export const getRecentChapters = (uid) => {
  try {
    const raw = localStorage.getItem(getReadingStorageKey(STORAGE_RECENT_CHAPTERS, uid));
    const list = raw ? JSON.parse(raw) : [];
    return Array.isArray(list) ? list : [];
  } catch (e) {
    return [];
  }
};

export const getCanonicalLastRead = (uid) => {
  const last = getLastReadChapter(uid);
  const recent = getRecentChapters(uid);
  let best = last && last.chapterId ? last : null;
  recent.forEach((item) => {
    if (!item || !item.chapterId) return;
    if (!best || readingTimestamp(item) > readingTimestamp(best)) {
      best = item;
    }
  });
  return best || null;
};

export const getReadChapterCount = (uid) => {
  const readMap = getReadChapters(uid);
  return Object.keys(readMap).filter((id) => Boolean(readMap[id])).length;
};

export const getReadingStats = (bibleChapters = [], uid) => {
  const readMap = getReadChapters(uid);
  const readChapterIds = Object.keys(readMap).filter((id) => Boolean(readMap[id]));
  const totalRead = readChapterIds.length;
  const totalChapters = bibleChapters.length || 1189;
  const percent = totalChapters > 0 ? ((totalRead / totalChapters) * 100).toFixed(1) : "0.0";

  // OT chapters: first 39 books (929 chapters in total)
  // NT chapters: remaining 27 books (260 chapters in total)
  let otRead = 0;
  let ntRead = 0;

  readChapterIds.forEach((id) => {
    // chapter id format: "bookIndex-chapterNum", e.g. "1-1"
    const bookIndex = parseInt(id.split("-")[0], 10);
    if (bookIndex >= 1 && bookIndex <= 39) {
      otRead += 1;
    } else if (bookIndex >= 40 && bookIndex <= 66) {
      ntRead += 1;
    }
  });

  return {
    totalRead,
    totalChapters,
    percent: Math.min(100, parseFloat(percent)),
    otRead,
    otTotal: 929,
    ntRead,
    ntTotal: 260,
  };
};

export const formatRelativeTime = (timestamp, isEn = false) => {
  if (!timestamp) return "";
  const diff = Date.now() - timestamp;
  const minutes = Math.floor(diff / (60 * 1000));
  const hours = Math.floor(diff / (60 * 60 * 1000));
  const days = Math.floor(diff / (24 * 60 * 60 * 1000));

  if (isEn) {
    if (minutes < 2) return "Just now";
    if (minutes < 60) return `${minutes} mins ago`;
    if (hours < 24) return `${hours} hr${hours > 1 ? "s" : ""} ago`;
    if (days === 1) return "Yesterday";
    if (days < 30) return `${days} days ago`;
    return "Recently";
  }

  if (minutes < 2) return "刚刚";
  if (minutes < 60) return `${minutes} 分钟前`;
  if (hours < 24) return `${hours} 小时前`;
  if (days === 1) return "昨天";
  if (days < 30) return `${days} 天前`;
  return "不久前";
};
